import {
  Component,
  ElementRef,
  input,
  output,
  viewChild,
  OnDestroy,
  ViewContainerRef,
  TemplateRef,
  HostListener,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { Overlay, OverlayRef, ConnectedPosition, FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

interface EmojiEvent {
  emoji: Emoji;
  $event: Event;
}

interface Emoji {
  name: string;
  unified: string;
  text: string;
  emoticons: string[];
  keywords: string[];
  sheet: number[];
  shortName: string;
  shortNames: string[];
  id: string;
  native: string;
  skinVariations: unknown[];
  hidden: unknown[];
  set: string;
  colons: string;
}

@Component({
  selector: 'so-emojis-ui',
  standalone: true,
  imports: [
    PickerComponent,
    CommonModule,
    SoSvgIconUI
  ],
  templateUrl: './so-emojis-ui.component.html',
  styleUrl: './so-emojis-ui.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoEmojisUI implements OnDestroy {
  position = input<'top' | 'left' | 'right'>('top');
  buttonColor = input<string>('--so-gray-thick');
  scale = input<number>(1);
  disabled = input<boolean>(false);
  excludedEmojis = input<string[]>([]);

  emojiSelected = output<string>();

  emojiButton = viewChild<ElementRef<HTMLElement>>('emojiButton');
  emojiPanel = viewChild<TemplateRef<unknown>>('emojiPanel');
  panelContainer = viewChild<ElementRef<HTMLElement>>('panelContainer');

  triangleDir = signal<'top' | 'bottom' | 'left' | 'right'>('top');

  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private overlayRef?: OverlayRef;

  readonly i18n = {
    search: 'Buscar',
    categories: {
      search: 'Resultados',
      recent: 'Recientes',
      people: 'Reacciones',
      nature: 'Naturaleza',
      foods: 'Comida',
      activity: 'Actividades',
      places: 'Lugares',
      objects: 'Objetos',
      symbols: 'Símbolos',
      flags: 'Banderas',
      custom: 'Personalizados',
    }
  };

  toggleEmojiOverlay(): void {
    if (this.overlayRef) {
      this.closeEmojiOverlay();
      return;
    }

    const buttonEl = this.emojiButton();
    const panelTpl = this.emojiPanel();
    if (!buttonEl || !panelTpl) return;

    const positions: ConnectedPosition[] = this.getPositionsFor(this.position());

    const strategy: FlexibleConnectedPositionStrategy = this.overlay.position()
      .flexibleConnectedTo(buttonEl.nativeElement)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false);

    strategy.positionChanges.subscribe(({ connectionPair }) => {
      const { overlayX, overlayY } = connectionPair;
      if (overlayX === 'start' && overlayY === 'center') {
        this.triangleDir.set('left');
      } else if (overlayX === 'end' && overlayY === 'center') {
        this.triangleDir.set('right');
      } else if (overlayY === 'bottom') {
        this.triangleDir.set('top');
      } else if (overlayY === 'top') {
        this.triangleDir.set('bottom');
      }
    });

    this.overlayRef = this.overlay.create({
      positionStrategy: strategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'emoji-overlay-panel'
    });

    const portal = new TemplatePortal(panelTpl, this.vcr);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => this.closeEmojiOverlay());
  }

  private getPositionsFor(pos: 'top' | 'left' | 'right'): ConnectedPosition[] {
    switch (pos) {
      case 'left':
        return [
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 }
        ];
      case 'right':
        return [
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 }
        ];
      default:
        return [
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 }
        ];
    }
  }

  addEmoji(event: EmojiEvent): void {
    const nuevoEmoji = String.fromCodePoint(parseInt(event.emoji.unified, 16));
    this.emojiSelected.emit(nuevoEmoji);
  }

  ngOnDestroy(): void {
    this.closeEmojiOverlay();
  }

  private closeEmojiOverlay(): void {
    if (!this.overlayRef) return;
    this.overlayRef.detach();
    this.overlayRef.dispose();
    this.overlayRef = undefined;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.overlayRef) return;

    const target = event.target as HTMLElement;
    const panelEl = this.panelContainer();
    const buttonEl = this.emojiButton();

    const clickedInsidePanel = panelEl?.nativeElement.contains(target);
    const clickedButton = buttonEl?.nativeElement.contains(target);

    if (!clickedInsidePanel && !clickedButton) {
      this.closeEmojiOverlay();
    }
  }

  getEmojisToShow(): (emoji: string) => boolean {
    const excluded = this.excludedEmojis();
    return (emoji: string) => !excluded.includes(emoji);
  }
}
